import React, { useEffect, useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { Receipt, Car, FileText, Camera, Loader2, Check, X, Trash2, Download, Route, Upload, Lock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import {
  getFraisStats, getFraisVehicule, updateFraisVehicule, uploadCarteGrise, getFraisFichierUrl,
  analyserJustificatif, getFraisNotes, createFraisNote, updateFraisNote, deleteFraisNote,
  calcDistanceTrajet, getFraisTrajets, createFraisTrajet, updateFraisTrajet, deleteFraisTrajet,
  getFraisExportPdfUrl, getProjets, getFraisConvivesSuggestions,
} from '../services/api';

const eur = (v) => `${(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const STATUTS = {
  a_valider: { label: 'À valider', cls: 'bg-amber-100 text-amber-800' },
  valide: { label: 'Validé', cls: 'bg-blue-100 text-blue-800' },
  rembourse: { label: 'Remboursé', cls: 'bg-emerald-100 text-emerald-800' },
  refuse: { label: 'Refusé', cls: 'bg-red-100 text-red-700' },
};
const INPUT = 'w-full h-10 px-3 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900';
const currentMonth = () => new Date().toISOString().slice(0, 7);

const StatutBadge = ({ statut }) => {
  const s = STATUTS[statut] || STATUTS.a_valider;
  return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${s.cls}`}>{s.label}</span>;
};

const AdminActions = ({ item, onStatut, onDelete, testPrefix }) => (
  <div className="flex items-center gap-1 shrink-0">
    {item.statut === 'a_valider' && (
      <>
        <button onClick={() => onStatut(item, 'valide')} className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded" title="Valider" data-testid={`${testPrefix}-valider-${item.id}`}><Check className="w-4 h-4" /></button>
        <button onClick={() => onStatut(item, 'refuse')} className="p-1.5 text-red-500 hover:bg-red-50 rounded" title="Refuser" data-testid={`${testPrefix}-refuser-${item.id}`}><X className="w-4 h-4" /></button>
      </>
    )}
    {item.statut !== 'rembourse' && (
      <button onClick={() => onDelete(item)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded" title="Supprimer" data-testid={`${testPrefix}-delete-${item.id}`}><Trash2 className="w-4 h-4" /></button>
    )}
  </div>
);

// Gros boutons (fiables sur mobile, contrairement aux <select> natifs iOS dans certains cas)
const Toggle = ({ options, value, onChange, testId }) => (
  <div className="flex rounded-lg border border-slate-300 overflow-hidden" data-testid={testId}>
    {options.map((o) => (
      <button key={o.value} type="button" onClick={() => onChange(o.value)} data-testid={`${testId}-${o.value}`}
        className={`flex-1 h-10 px-2 text-sm font-medium transition-colors ${value === o.value ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
        {o.label}
      </button>
    ))}
  </div>
);

const AffectationFields = ({ value, projetId, onChange, projets }) => {
  const enseignes = [...new Map(projets.filter((p) => p.enseigne_id).map((p) => [p.enseigne_id, p.enseigne_nom])).entries()].sort((a, b) => (a[1] || '').localeCompare(b[1] || ''));
  const current = projets.find((p) => p.id === projetId);
  const [enseigneId, setEnseigneId] = useState(current?.enseigne_id || '');
  const [filtre, setFiltre] = useState('');
  useEffect(() => { if (current) setEnseigneId(current.enseigne_id); }, [current]);
  const enseignesVisibles = enseignes.filter(([, nom]) => (nom || '').toLowerCase().includes(filtre.trim().toLowerCase()));
  const projetsEnseigne = projets.filter((p) => p.enseigne_id === enseigneId);
  const enseigneNom = enseignes.find(([id]) => id === enseigneId)?.[1];
  const chip = (active) => `h-10 px-3 rounded-lg border text-sm text-left truncate transition-colors ${active ? 'bg-slate-900 text-white border-slate-900' : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'}`;
  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Affectation</label>
        <Toggle testId="frais-affectation" value={value} onChange={(v) => onChange({ affectation: v, projet_id: v === 'projet' ? projetId : null })}
          options={[{ value: 'frais_generaux', label: 'Frais généraux entreprise' }, { value: 'projet', label: 'Chantier / projet' }]} />
      </div>
      {value === 'projet' && (
        <div className="space-y-3">
          <div data-testid="frais-enseigne">
            <label className="block text-xs font-medium text-slate-600 mb-1">1. Enseigne {enseigneNom && <span className="text-slate-900 font-semibold">: {enseigneNom}</span>}</label>
            {enseigneId ? (
              <button type="button" onClick={() => { setEnseigneId(''); onChange({ affectation: 'projet', projet_id: null }); }} className="h-10 px-3 rounded-lg border border-slate-300 bg-white text-sm text-slate-600 hover:bg-slate-50" data-testid="frais-enseigne-changer">↺ Changer d'enseigne</button>
            ) : (
              <>
                <input value={filtre} onChange={(e) => setFiltre(e.target.value)} placeholder="Rechercher une enseigne…" className={`${INPUT} mb-2`} data-testid="frais-enseigne-filtre" />
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-56 overflow-y-auto pr-1">
                  {enseignesVisibles.map(([id, nom]) => (
                    <button key={id} type="button" onClick={() => { setEnseigneId(id); setFiltre(''); onChange({ affectation: 'projet', projet_id: null }); }} className={chip(false)} data-testid={`frais-enseigne-${id}`}>{nom}</button>
                  ))}
                  {enseignesVisibles.length === 0 && <p className="col-span-full text-xs text-slate-400">Aucune enseigne ne correspond</p>}
                </div>
              </>
            )}
          </div>
          {enseigneId && (
            <div data-testid="frais-projet">
              <label className="block text-xs font-medium text-slate-600 mb-1">2. Projet / ville</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                {projetsEnseigne.map((p) => (
                  <button key={p.id} type="button" onClick={() => onChange({ affectation: 'projet', projet_id: p.id })} className={chip(projetId === p.id)} data-testid={`frais-projet-${p.id}`}>
                    {p.ville}{p.statut ? <span className={`ml-1 text-xs ${projetId === p.id ? 'text-slate-300' : 'text-slate-400'}`}>({p.statut.replace(/_/g, ' ')})</span> : ''}
                  </button>
                ))}
                {projetsEnseigne.length === 0 && <p className="text-xs text-slate-400">Aucun projet ouvert pour cette enseigne</p>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const TAUX_TVA = [20, 10, 5.5, 0];
const tvaFromTtc = (ttc, taux) => { const t = parseFloat(ttc) || 0; return taux ? Math.round((t - t / (1 + taux / 100)) * 100) / 100 : 0; };

/* Repas : nombre de convives + avec qui (exigé par le comptable). Texte prérempli selon le chantier, toujours modifiable. */
const CONVIVES_TYPES = [{ value: 'client', label: 'Client' }, { value: 'fournisseur', label: 'Fournisseur / artisan' }, { value: 'equipe', label: 'Équipe MAAG' }, { value: 'autre', label: 'Autre' }];
const CONVIVES_DEFAULT = { nb_convives: 2, convives_type: 'client', convives: '', convives_auto: true };
const convivesPrefill = (type, sugg) => {
  if (type === 'client') return sugg?.enseigne_nom ? `Client ${sugg.enseigne_nom}${sugg.ville ? ` ${sugg.ville}` : ''} – ` : 'Client ';
  if (type === 'fournisseur') return 'Fournisseur ';
  if (type === 'equipe') return 'Équipe MAAG';
  return '';
};

const ConvivesFields = ({ form, setForm, projetId }) => {
  const [sugg, setSugg] = useState(null);
  useEffect(() => {
    if (!projetId) { setSugg(null); return; }
    getFraisConvivesSuggestions(projetId).then((r) => setSugg(r.data)).catch(() => setSugg(null));
  }, [projetId]);
  // Re-préremplit le texte quand le type ou le chantier change, tant que l'utilisateur n'a pas écrit lui-même
  useEffect(() => {
    if (form.convives_auto === false) return;
    const txt = convivesPrefill(form.convives_type, sugg);
    setForm((f) => (f.convives === txt ? f : { ...f, convives: txt }));
  }, [form.convives_type, form.convives_auto, sugg, setForm]);

  const setType = (t) => setForm((f) => ({ ...f, convives_type: t, convives_auto: true }));
  const pickFournisseur = (nom) => setForm((f) => ({ ...f, convives: `Fournisseur ${nom}`, convives_auto: false }));
  const nb = form.nb_convives ?? '';
  return (
    <div className="sm:col-span-2 md:col-span-4 grid grid-cols-1 sm:grid-cols-2 gap-3 bg-white/60 border border-amber-200 rounded-lg p-3" data-testid="frais-convives">
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Nombre de personnes (vous inclus)</label>
        <div className="flex gap-1">
          {[1, 2, 3, 4].map((k) => (
            <button key={k} type="button" onClick={() => setForm({ ...form, nb_convives: k })} data-testid={`frais-convives-nb-${k}`}
              className={`h-10 w-10 rounded-lg border text-sm font-semibold ${nb === k ? 'bg-slate-900 text-white border-slate-900' : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'}`}>{k}</button>
          ))}
          <input type="text" inputMode="numeric" value={nb} onChange={(e) => setForm({ ...form, nb_convives: parseInt(e.target.value, 10) || '' })} placeholder="+" className={`${INPUT} w-16 text-center font-mono`} data-testid="frais-convives-nb" />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Avec qui ?</label>
        <Toggle testId="frais-convives-type" value={form.convives_type || 'client'} onChange={setType} options={CONVIVES_TYPES} />
      </div>
      <div className="sm:col-span-2">
        <input value={form.convives || ''} onChange={(e) => setForm({ ...form, convives: e.target.value, convives_auto: false })} placeholder="Ex. Client HAPPY CASH – Benoît" className={INPUT} data-testid="frais-convives-texte" />
        {form.convives_type === 'fournisseur' && sugg?.fournisseurs?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2" data-testid="frais-convives-fournisseurs">
            {sugg.fournisseurs.map((f) => <button key={f} type="button" onClick={() => pickFournisseur(f)} className="px-2 py-1 text-xs rounded-full border border-slate-300 bg-white hover:bg-slate-100" data-testid={`frais-convives-fournisseur-${f}`}>{f}</button>)}
          </div>
        )}
        {form.convives_type === 'fournisseur' && projetId && sugg && sugg.fournisseurs?.length === 0 && <p className="text-xs text-slate-400 mt-1">Aucun fournisseur saisi sur ce chantier — écrivez le nom.</p>}
        {form.convives_type === 'client' && !projetId && <p className="text-xs text-slate-400 mt-1">Choisissez le chantier ci-dessus : le client sera prérempli.</p>}
      </div>
    </div>
  );
};

/* ---------------- Notes de frais ---------------- */
const NotesTab = ({ mois, projets, categories, isAdmin, onChanged }) => {
  const [notes, setNotes] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [form, setForm] = useState(null);
  const fileRef = useRef();

  const load = useCallback(async () => {
    const res = await getFraisNotes(mois).catch(() => ({ data: [] }));
    setNotes(res.data || []);
  }, [mois]);
  useEffect(() => { load(); }, [load]);

  const handleFile = async (file) => {
    if (!file) return;
    setAnalyzing(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await analyserJustificatif(fd);
      const taux = TAUX_TVA.includes(res.data.taux_tva) ? res.data.taux_tva : null;
      setForm({ ...res.data, taux_tva: taux, mode_paiement: 'maag', affectation: 'frais_generaux', projet_id: null, commentaire: res.data.description || '', ...CONVIVES_DEFAULT });
      toast.success('Justificatif analysé — vérifiez puis enregistrez');
    } catch (e) {
      toast.error(e.response?.data?.detail || "Analyse impossible");
    } finally {
      setAnalyzing(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const save = async () => {
    if (!form.montant_ttc || form.montant_ttc <= 0) return toast.error('Montant TTC requis');
    if (form.affectation === 'projet' && !form.projet_id) return toast.error('Choisissez le projet');
    try {
      const { convives_auto, ...payload } = form;
      const repas = form.categorie === 'repas';
      await createFraisNote({ ...payload, montant_ttc: parseFloat(form.montant_ttc), montant_tva: parseFloat(form.montant_tva) || 0, taux_tva: form.taux_tva ?? null,
        nb_convives: repas && parseInt(form.nb_convives, 10) > 0 ? parseInt(form.nb_convives, 10) : null, convives_type: repas ? (form.convives_type || 'client') : null, convives: repas ? (form.convives || '') : '' });
      toast.success('Note de frais enregistrée');
      setForm(null); load(); onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };
  const setStatut = async (n, statut) => { await updateFraisNote(n.id, { statut }); load(); onChanged(); };
  const remove = async (n) => { if (!window.confirm('Supprimer cette note de frais ?')) return; await deleteFraisNote(n.id); load(); onChanged(); };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <input ref={fileRef} type="file" accept="image/*,.pdf" capture="environment" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="frais-scan-input" />
        <button onClick={() => fileRef.current?.click()} disabled={analyzing} className="flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white rounded-lg text-sm font-semibold hover:bg-slate-800 disabled:opacity-60" data-testid="frais-scan-btn">
          {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />} {analyzing ? 'Analyse du ticket…' : 'Scanner / importer un justificatif'}
        </button>
        <button onClick={() => setForm({ date: new Date().toISOString().slice(0, 10), commercant: '', montant_ttc: '', montant_tva: '', taux_tva: 20, categorie: 'autre', mode_paiement: 'maag', affectation: 'frais_generaux', projet_id: null, commentaire: '', ...CONVIVES_DEFAULT })} className="px-4 py-2.5 border border-slate-300 rounded-lg text-sm hover:bg-slate-50" data-testid="frais-manual-btn">Saisie sans justificatif</button>
      </div>

      {form && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3" data-testid="frais-note-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
            <div><label className="block text-xs font-medium text-slate-600 mb-1">Date</label><input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={INPUT} data-testid="frais-note-date" /></div>
            <div><label className="block text-xs font-medium text-slate-600 mb-1">Commerçant</label><input value={form.commercant} onChange={(e) => setForm({ ...form, commercant: e.target.value })} className={INPUT} data-testid="frais-note-commercant" /></div>
            <div><label className="block text-xs font-medium text-slate-600 mb-1">Montant TTC (€)</label><input type="text" inputMode="decimal" value={form.montant_ttc} onChange={(e) => { const ttc = e.target.value.replace(',', '.'); setForm({ ...form, montant_ttc: ttc, montant_tva: form.taux_tva != null ? tvaFromTtc(ttc, form.taux_tva) : form.montant_tva }); }} className={`${INPUT} font-mono`} data-testid="frais-note-ttc" /></div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Catégorie</label>
              <select value={form.categorie} onChange={(e) => setForm({ ...form, categorie: e.target.value })} className={INPUT} data-testid="frais-note-categorie">
                {Object.entries(categories).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-slate-600 mb-1">Taux de TVA</label>
              <div className="flex gap-2 items-center">
                <Toggle testId="frais-note-taux" value={form.taux_tva ?? 'multi'} onChange={(v) => setForm({ ...form, taux_tva: v === 'multi' ? null : v, montant_tva: v === 'multi' ? form.montant_tva : tvaFromTtc(form.montant_ttc, v) })}
                  options={[...TAUX_TVA.map((t) => ({ value: t, label: `${t} %` })), { value: 'multi', label: 'Mixte' }]} />
              </div>
            </div>
            <div className="sm:col-span-2"><label className="block text-xs font-medium text-slate-600 mb-1">dont TVA (€){form.taux_tva != null && <span className="text-slate-400"> — calculée à {form.taux_tva} %, modifiable</span>}</label><input type="text" inputMode="decimal" value={form.montant_tva} onChange={(e) => setForm({ ...form, montant_tva: e.target.value.replace(',', '.') })} className={`${INPUT} font-mono`} data-testid="frais-note-tva" /></div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-slate-600 mb-1">Payé par</label>
              <Toggle testId="frais-note-paiement" value={form.mode_paiement} onChange={(v) => setForm({ ...form, mode_paiement: v })}
                options={[{ value: 'maag', label: 'Entreprise MAAG (carte / compte)' }, { value: 'avance_perso', label: 'Perso (à me rembourser)' }]} />
            </div>
            <div className="sm:col-span-2 md:col-span-4"><AffectationFields value={form.affectation} projetId={form.projet_id} projets={projets} onChange={(p) => setForm({ ...form, ...p })} /></div>
            {form.categorie === 'repas' && <ConvivesFields form={form} setForm={setForm} projetId={form.affectation === 'projet' ? form.projet_id : null} />}
          </div>
          <input value={form.commentaire} onChange={(e) => setForm({ ...form, commentaire: e.target.value })} placeholder="Commentaire (facultatif)" className={INPUT} />
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-500">{form.justificatif_nom ? `📎 ${form.justificatif_nom}` : 'Aucun justificatif joint'}</span>
            <div className="flex gap-2">
              <button onClick={() => setForm(null)} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Annuler</button>
              <button onClick={save} className="px-4 py-2 text-sm font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700" data-testid="frais-note-save">Enregistrer</button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr>
            <th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Commerçant</th><th className="text-left px-3 py-2">Catégorie</th><th className="text-left px-3 py-2">Affectation</th><th className="text-left px-3 py-2">Payé par</th><th className="text-right px-3 py-2">TVA</th><th className="text-right px-3 py-2">TTC</th><th className="text-left px-3 py-2">Statut</th><th className="px-3 py-2"></th>
          </tr></thead>
          <tbody>
            {notes.length === 0 && <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400">Aucune note de frais ce mois-ci</td></tr>}
            {notes.map((n) => (
              <tr key={n.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`frais-note-row-${n.id}`}>
                <td className="px-3 py-2 whitespace-nowrap">{n.date}</td>
                <td className="px-3 py-2 font-medium">{n.commercant || '—'}{n.justificatif_path && <a href={getFraisFichierUrl(n.justificatif_path)} target="_blank" rel="noreferrer" className="ml-2 text-slate-400 hover:text-blue-600" title="Voir le justificatif"><FileText className="w-3.5 h-3.5 inline" /></a>}
                  {n.categorie === 'repas' && (n.nb_convives || n.convives) && <span className="block text-xs font-normal text-slate-500" data-testid={`frais-note-convives-${n.id}`}>{[n.nb_convives ? `${n.nb_convives} pers.` : null, n.convives].filter(Boolean).join(' – ')}</span>}</td>
                <td className="px-3 py-2">{categories[n.categorie] || n.categorie}</td>
                <td className="px-3 py-2 text-slate-600">{n.projet_nom || 'Frais généraux'}</td>
                <td className="px-3 py-2">{n.mode_paiement === 'avance_perso' ? <span className="text-amber-700 font-medium">Avance perso</span> : 'MAAG'}{isAdmin && n.auteur_nom && <span className="block text-xs text-slate-400">{n.auteur_nom}</span>}</td>
                <td className="px-3 py-2 text-right font-mono text-slate-600">{eur(n.montant_tva)}{n.taux_tva != null && <span className="block text-xs text-slate-400">{n.taux_tva} %</span>}</td>
                <td className="px-3 py-2 text-right font-mono">{eur(n.montant_ttc)}</td>
                <td className="px-3 py-2"><StatutBadge statut={n.statut} /></td>
                <td className="px-3 py-2">{isAdmin && <AdminActions item={n} onStatut={setStatut} onDelete={remove} testPrefix="note" />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ---------------- Kilométrique ---------------- */
const TrajetsTab = ({ mois, projets, isAdmin, orsDisponible, vehicule, onChanged }) => {
  const [trajets, setTrajets] = useState([]);
  const defaultDepart = vehicule?.adresse_depart || '';
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), depart: defaultDepart, arrivee: '', aller_retour: true, distance_km: '', source: 'manuel', affectation: 'frais_generaux', projet_id: null, commentaire: '' });
  useEffect(() => { setForm((f) => (f.depart ? f : { ...f, depart: defaultDepart })); }, [defaultDepart]);
  const [calculating, setCalculating] = useState(false);

  const load = useCallback(async () => {
    const res = await getFraisTrajets(mois).catch(() => ({ data: [] }));
    setTrajets(res.data || []);
  }, [mois]);
  useEffect(() => { load(); }, [load]);

  const calc = async () => {
    if (!form.depart || !form.arrivee) return toast.error('Saisissez le départ et l\'arrivée');
    setCalculating(true);
    try {
      const res = await calcDistanceTrajet(form.depart, form.arrivee);
      setForm({ ...form, distance_km: String(res.data.distance_km), source: 'ors' });
      toast.success(`${res.data.distance_km} km (aller)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Calcul impossible — saisissez les km');
    } finally { setCalculating(false); }
  };
  const save = async () => {
    const km = parseFloat(String(form.distance_km).replace(',', '.'));
    if (!km || km <= 0) return toast.error('Distance en km requise');
    if (form.affectation === 'projet' && !form.projet_id) return toast.error('Choisissez le projet');
    try {
      const res = await createFraisTrajet({ ...form, distance_km: km });
      toast.success(`Trajet enregistré : ${res.data.distance_totale_km} km → ${eur(res.data.montant)}`);
      setForm({ ...form, depart: defaultDepart, arrivee: '', distance_km: '', source: 'manuel', commentaire: '' });
      load(); onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };
  const setStatut = async (t, statut) => { await updateFraisTrajet(t.id, { statut }); load(); onChanged(); };
  const remove = async (t) => { if (!window.confirm('Supprimer ce trajet ?')) return; await deleteFraisTrajet(t.id); load(); onChanged(); };
  const reaffecter = async (t, projet_id) => { await updateFraisTrajet(t.id, { affectation: projet_id ? 'projet' : 'frais_generaux', projet_id: projet_id || null }); load(); toast.success('Trajet réaffecté'); };

  if (!vehicule?.carte_grise_path) {
    return <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center text-sm text-amber-800" data-testid="frais-no-vehicule">
      <Lock className="w-5 h-5 mx-auto mb-2" />Ajoutez d'abord la <b>carte grise</b> du véhicule (onglet Véhicule) : elle est obligatoire pour calculer les indemnités kilométriques.
    </div>;
  }

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3" data-testid="frais-trajet-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 gap-3">
          <div><label className="block text-xs font-medium text-slate-600 mb-1">Date</label><input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={INPUT} data-testid="trajet-date" /></div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-slate-600 mb-1">Départ {defaultDepart && form.depart !== defaultDepart && <button type="button" onClick={() => setForm({ ...form, depart: defaultDepart })} className="ml-1 text-blue-600 hover:underline" data-testid="trajet-depart-reset">↺ adresse par défaut</button>}</label>
            <input value={form.depart} onChange={(e) => setForm({ ...form, depart: e.target.value })} placeholder={defaultDepart || "12 rue des Lilas, Lyon"} className={INPUT} data-testid="trajet-depart" />
          </div>
          <div className="sm:col-span-2"><label className="block text-xs font-medium text-slate-600 mb-1">Arrivée</label><input value={form.arrivee} onChange={(e) => setForm({ ...form, arrivee: e.target.value })} placeholder="Centre commercial Part-Dieu, Lyon" className={INPUT} data-testid="trajet-arrivee" /></div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Km (aller)</label>
            <div className="flex gap-1">
              <input type="text" inputMode="decimal" value={form.distance_km} onChange={(e) => setForm({ ...form, distance_km: e.target.value, source: 'manuel' })} className={`${INPUT} font-mono`} data-testid="trajet-km" />
              {orsDisponible && <button onClick={calc} disabled={calculating} title="Calculer la distance" className="h-10 px-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60" data-testid="trajet-calc-btn">{calculating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Route className="w-4 h-4" />}</button>}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm md:col-span-2"><input type="checkbox" checked={form.aller_retour} onChange={(e) => setForm({ ...form, aller_retour: e.target.checked })} className="rounded" data-testid="trajet-ar" /> Aller-retour (km × 2)</label>
          <div className="sm:col-span-2 md:col-span-4"><AffectationFields value={form.affectation} projetId={form.projet_id} projets={projets} onChange={(p) => setForm({ ...form, ...p })} /></div>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-500">Barème URSSAF {vehicule.cv_fiscaux} CV{vehicule.motorisation === 'electrique' ? ' · électrique (+20 %)' : ''} · tranches annuelles cumulées{!orsDisponible && ' · distance saisie manuellement'}</span>
          <button onClick={save} className="px-4 py-2 text-sm font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700" data-testid="trajet-save">Enregistrer le trajet</button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr>
            <th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Trajet</th><th className="text-right px-3 py-2">Km</th><th className="text-left px-3 py-2">Affectation</th><th className="text-right px-3 py-2">Indemnité</th><th className="text-left px-3 py-2">Statut</th><th className="px-3 py-2"></th>
          </tr></thead>
          <tbody>
            {trajets.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400">Aucun trajet ce mois-ci</td></tr>}
            {trajets.map((t) => (
              <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`trajet-row-${t.id}`}>
                <td className="px-3 py-2 whitespace-nowrap">{t.date}</td>
                <td className="px-3 py-2">{t.depart} → {t.arrivee}{t.aller_retour && <span className="ml-1 text-xs text-slate-400">(A/R)</span>}{isAdmin && t.auteur_nom && <span className="block text-xs text-slate-400">{t.auteur_nom}</span>}</td>
                <td className="px-3 py-2 text-right font-mono">{t.distance_totale_km}</td>
                <td className="px-3 py-2">
                  {t.statut === 'rembourse' ? (t.projet_nom || 'Frais généraux') : (
                    <select value={t.projet_id || ''} onChange={(e) => reaffecter(t, e.target.value)} className="h-8 px-2 rounded border border-slate-200 text-xs bg-transparent" data-testid={`trajet-reaffect-${t.id}`}>
                      <option value="">Frais généraux</option>
                      {projets.map((p) => <option key={p.id} value={p.id}>{p.enseigne_nom} {p.ville}</option>)}
                    </select>
                  )}
                </td>
                <td className="px-3 py-2 text-right font-mono font-semibold">{eur(t.montant)}</td>
                <td className="px-3 py-2"><StatutBadge statut={t.statut} /></td>
                <td className="px-3 py-2">{isAdmin && <AdminActions item={t} onStatut={setStatut} onDelete={remove} testPrefix="trajet" />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ---------------- Véhicule ---------------- */
const VehiculeTab = ({ vehicule, onSaved }) => {
  const [form, setForm] = useState(vehicule);
  const [uploading, setUploading] = useState(false);
  useEffect(() => setForm(vehicule), [vehicule]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await uploadCarteGrise(fd);
      onSaved(res.data);
      toast.success('Carte grise enregistrée — puissance fiscale et motorisation pré-remplies');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); } finally { setUploading(false); }
  };
  const save = async () => {
    const res = await updateFraisVehicule({ nom: form.nom, immatriculation: form.immatriculation, cv_fiscaux: parseInt(form.cv_fiscaux, 10) || 5, motorisation: form.motorisation, adresse_depart: form.adresse_depart || '' });
    onSaved(res.data); toast.success('Véhicule enregistré');
  };
  if (!form) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4 max-w-2xl" data-testid="frais-vehicule-form">
      <div className={`rounded-lg p-4 border ${form.carte_grise_path ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
        <p className="text-sm font-semibold mb-2">{form.carte_grise_path ? '✓ Carte grise enregistrée' : '⚠️ Carte grise obligatoire'}</p>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 px-3 py-2 bg-slate-900 text-white rounded-lg text-sm cursor-pointer hover:bg-slate-800">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {form.carte_grise_path ? 'Remplacer le scan' : 'Scanner / importer la carte grise'}
            <input type="file" accept="image/*,.pdf" capture="environment" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="carte-grise-input" />
          </label>
          {form.carte_grise_path && <a href={getFraisFichierUrl(form.carte_grise_path)} target="_blank" rel="noreferrer" className="text-sm text-blue-700 underline" data-testid="carte-grise-link">Voir le scan</a>}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="block text-xs font-medium text-slate-600 mb-1">Véhicule</label><input value={form.nom || ''} onChange={(e) => setForm({ ...form, nom: e.target.value })} placeholder="Renault Kangoo" className={INPUT} data-testid="vehicule-nom" /></div>
        <div><label className="block text-xs font-medium text-slate-600 mb-1">Immatriculation</label><input value={form.immatriculation || ''} onChange={(e) => setForm({ ...form, immatriculation: e.target.value })} placeholder="AB-123-CD" className={INPUT} data-testid="vehicule-immat" /></div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Puissance fiscale (CV)</label>
          <select value={form.cv_fiscaux || 5} onChange={(e) => setForm({ ...form, cv_fiscaux: e.target.value })} className={INPUT} data-testid="vehicule-cv">
            {[3, 4, 5, 6, 7].map((cv) => <option key={cv} value={cv}>{cv} CV{cv === 7 ? ' et plus' : ''}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Motorisation</label>
          <select value={form.motorisation || 'thermique'} onChange={(e) => setForm({ ...form, motorisation: e.target.value })} className={INPUT} data-testid="vehicule-motorisation">
            <option value="thermique">Thermique (essence / diesel)</option>
            <option value="hybride">Hybride</option>
            <option value="electrique">Électrique (+20 % barème)</option>
          </select>
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Adresse de départ par défaut (domicile / dépôt)</label>
        <input value={form.adresse_depart || ''} onChange={(e) => setForm({ ...form, adresse_depart: e.target.value })} placeholder="12 rue des Lilas, 69000 Lyon" className={INPUT} data-testid="vehicule-adresse-depart" />
        <p className="text-xs text-slate-500 mt-1">Pré-remplie dans le champ « Départ » de chaque nouveau trajet (modifiable à chaque fois).</p>
      </div>
      <div className="flex justify-end"><button onClick={save} className="px-4 py-2 text-sm font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700" data-testid="vehicule-save">Enregistrer</button></div>
    </div>
  );
};

/* ---------------- Page ---------------- */
export default function FraisPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.is_admin;
  const [tab, setTab] = useState('notes');
  const [mois, setMois] = useState(currentMonth());
  const [stats, setStats] = useState(null);
  const [vehicule, setVehicule] = useState(null);
  const [projets, setProjets] = useState([]);

  const loadStats = useCallback(async () => {
    const res = await getFraisStats(mois).catch(() => ({ data: null }));
    setStats(res.data);
  }, [mois]);
  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => {
    getFraisVehicule().then((r) => setVehicule(r.data)).catch(() => {});
    getProjets().then((r) => setProjets((r.data || []).filter((p) => !p.is_cloture))).catch(() => {});
  }, []);

  const tabs = [
    { id: 'notes', label: 'Notes de frais', icon: Receipt },
    { id: 'km', label: 'Kilométrique', icon: Route },
    { id: 'vehicule', label: 'Véhicule', icon: Car },
  ];

  return (
    <div className="space-y-6" data-testid="frais-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Frais</h1>
          <p className="text-sm text-slate-500">Notes de frais, indemnités kilométriques et export mensuel pour le comptable</p>
        </div>
        <div className="flex items-center gap-2">
          <input type="month" value={mois} onChange={(e) => setMois(e.target.value)} className={`${INPUT} w-44`} data-testid="frais-mois" />
          <a href={getFraisExportPdfUrl(mois)} className="flex items-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700" data-testid="frais-export-pdf" title="PDF récapitulatif + justificatifs du mois">
            <Download className="w-4 h-4" /> PDF comptable
          </a>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 md:gap-3" data-testid="frais-stats">
          {[['Dépenses TTC', eur(stats.notes_total_ttc)], ['Avances perso', eur(stats.avances_perso)], ['Indemnités km', eur(stats.ik_total)], ['Km du mois', `${stats.km_mois} km`], ['Km cumulés année', `${stats.km_annee} km`]].map(([l, v]) => (
            <div key={l} className="bg-white border border-slate-200 rounded-xl p-3"><p className="text-xs text-slate-500">{l}</p><p className="text-base md:text-lg font-bold text-slate-900 truncate">{v}</p></div>
          ))}
        </div>
      )}

      <div className="flex gap-1 border-b border-slate-200">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${tab === t.id ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-800'}`} data-testid={`frais-tab-${t.id}`}>
            <t.icon className="w-4 h-4" /> {t.label}{t.id === 'vehicule' && vehicule && !vehicule.carte_grise_path && <span className="w-2 h-2 rounded-full bg-amber-500" />}
          </button>
        ))}
      </div>

      {tab === 'notes' && stats && <NotesTab mois={mois} projets={projets} categories={stats.categories} isAdmin={isAdmin} onChanged={loadStats} />}
      {tab === 'km' && stats && <TrajetsTab mois={mois} projets={projets} isAdmin={isAdmin} orsDisponible={stats.ors_disponible} vehicule={vehicule} onChanged={loadStats} />}
      {tab === 'vehicule' && vehicule && <VehiculeTab vehicule={vehicule} onSaved={setVehicule} />}
    </div>
  );
}
